import { useState, useEffect } from 'react';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { planHistoryStore, type PlanHistoryEntry } from '@/services/planHistoryStore';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';

interface PlanHistoryListProps {
  org: string;
  activePlanId?: string;
  onSelect: (entry: PlanHistoryEntry) => void;
  refreshTrigger?: number;
  /** Opens the Create view — the empty state's CTA. */
  onCreate?: () => void;
}

export function PlanHistoryList({ org, activePlanId, onSelect, refreshTrigger, onCreate }: PlanHistoryListProps) {
  const [entries, setEntries] = useState<PlanHistoryEntry[]>([]);

  useEffect(() => {
    setEntries(planHistoryStore.list(org));
  }, [org, refreshTrigger]);

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    planHistoryStore.remove(org, id);
    setEntries(planHistoryStore.list(org));
  };

  // A proper empty state, not a blank panel — the Drafts view is a top-level
  // destination on the Plan pillar, so it must explain itself when unused.
  if (entries.length === 0) {
    return (
      <PageEmptyState
        icon={FileText}
        title="There are no plan drafts yet"
        description="Every plan you generate is saved here automatically, so you can come back, refine it and populate a board later. Nothing reaches GitHub until you do."
      >
        {onCreate && (
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700"
          >
            <Plus className="h-4 w-4" />
            Create a Plan
          </button>
        )}
      </PageEmptyState>
    );
  }

  return (
    <>
      <div className="px-3 pt-2 pb-1">
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
          Plan History
        </span>
      </div>
      {entries.map((entry) => (
        <button
          key={entry.id}
          onClick={() => onSelect(entry)}
          className={`group w-full text-left px-3 py-2.5 rounded-md transition-colors ${
            activePlanId === entry.id
              ? 'bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800'
              : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate flex-1">
              {entry.plan.productName}
            </span>
            <button
              onClick={(e) => handleDelete(e, entry.id)}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
              title="Delete plan"
            >
              <Trash2 className="w-3 h-3 text-red-400 hover:text-red-600" />
            </button>
          </div>
          {entry.description && (
            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate ml-5.5 mb-1">
              {entry.description}
            </p>
          )}
          <div className="flex items-center gap-2 ml-5.5">
            <span className="text-[10px] text-slate-400 truncate">
              {entry.plan.epics.length} epics
            </span>
            <span className="text-[10px] text-slate-400">
              {new Date(entry.createdAt).toLocaleDateString()}
            </span>
          </div>
        </button>
      ))}
    </>
  );
}
